import { c as _c } from "react-compiler-runtime";
import { Container } from "@/components/primitives";
import { BCP47LangCode } from "@/core/i18n";
import { getPageJsonLd, JsonLd } from "@/core/seo";

/**
 * The slice of a compiled document this component reads. Structural on purpose: any
 * compiled-markdown source whose output carries these fields can render here without
 * this primitive knowing which content domain produced it.
 */
export type TextDocumentSource = {
  /** The locale that was requested. */
  readonly locale: BCP47LangCode;
  /** The locale the copy is actually written in — differs from `locale` under a fallback. */
  readonly contentLocale: BCP47LangCode;
  /** Compiled HTML. Only inject output from a renderer that escapes raw HTML. */
  readonly html: string;
  readonly frontmatter: {
    readonly title: string;
    readonly description: string;
    /** ISO `YYYY-MM-DD`. */
    readonly effectiveDate: string;
    /** Copy not cleared to serve; rendered with a visible banner. */
    readonly draft?: boolean;
  };
};
export type TextDocumentProps = {
  readonly document: TextDocumentSource;
  readonly lang: BCP47LangCode;
  /** The page's own path without the locale prefix — e.g. `/privacy`. */
  readonly pathname: string;
};

/**
 * Renders one compiled markdown document as a page.
 *
 * The HTML comes from `generate:legal-content`, whose parser escapes raw HTML and refuses
 * any link protocol outside its allowlist — so the only markup here is markup the compiler
 * itself emitted from committed, PR-reviewed copy. That is what makes the injection safe.
 */
export default function TextDocument(t0) {
  const $ = _c(29);
  const {
    document,
    lang,
    pathname
  } = t0;
  const {
    frontmatter,
    contentLocale,
    locale
  } = document;
  const t1 = `/${lang}${pathname}`;
  let t2;
  if ($[0] !== frontmatter.description || $[1] !== frontmatter.title || $[2] !== lang || $[3] !== t1) {
    t2 = getPageJsonLd({
      lang,
      description: frontmatter.description,
      name: frontmatter.title,
      pathname: t1
    });
    $[0] = frontmatter.description;
    $[1] = frontmatter.title;
    $[2] = lang;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const structuredData = t2;
  let t3;
  if ($[5] !== frontmatter.draft) {
    t3 = frontmatter.draft ? <p className="type-body-md mb-8 border-l-4 border-amber-500 bg-amber-50 px-4 py-3" role="status">This document is a draft and has not been cleared to publish.</p> : null;
    $[5] = frontmatter.draft;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== document.html) {
    t4 = {
      __html: document.html
    };
    $[7] = document.html;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== contentLocale || $[10] !== t4) {
    t5 = <div className="flex flex-col gap-4" dangerouslySetInnerHTML={t4} lang={contentLocale} />;
    $[9] = contentLocale;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== frontmatter.effectiveDate) {
    t6 = <p>Effective <time dateTime={frontmatter.effectiveDate}>{frontmatter.effectiveDate}</time>.</p>;
    $[12] = frontmatter.effectiveDate;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== contentLocale || $[15] !== locale) {
    t7 = contentLocale === locale ? null : <p lang={contentLocale}>This document is currently available in {contentLocale} only.</p>;
    $[14] = contentLocale;
    $[15] = locale;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t6 || $[18] !== t7) {
    t8 = <footer className="type-body-sm text-lede-foreground mt-12">{t6}{t7}</footer>;
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== t3 || $[21] !== t5 || $[22] !== t8) {
    t9 = <main data-testid="TextDocument">{t3}{t5}{t8}</main>;
    $[20] = t3;
    $[21] = t5;
    $[22] = t8;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== structuredData) {
    t10 = <JsonLd data={structuredData} id="text-document-json-ld" />;
    $[24] = structuredData;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== t10 || $[27] !== t9) {
    t11 = <Container className="py-16">{t9}{t10}</Container>;
    $[26] = t10;
    $[27] = t9;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  return t11;
}